import { useEffect, useState } from 'react';
import {
  Button,
  ButtonSet,
  Slider,
  Tooltip,
  ModalWrapper,
  TextInput,
  Accordion,
  AccordionItem,
} from 'carbon-components-react';
import { PieChart } from '@carbon/charts-react';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from '../locales/messages';
import { swapCountryAndLanguage } from '../utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import './WatsonROICalculator.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import {
  LangDefaults,
  determineCountry,
  useLang,
} from 'src/common/hooks/widgets/useLang';
import countryList from '../services/currencies.json';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { usePrice } from '../hooks/usePrice';
import Cookies from 'js-cookie';
import {
  UT30WAROI,
  colorDigital,
  colorVoice,
  defaultClientsAmount,
  defaultVoiceAmount,
  maxClientsAmount,
  maxVoiceAmount,
  minVoiceAmount,
} from '../constants/defaults';
import * as URX_FORM from '../constants/urx-form';
import { Helmet } from 'react-helmet';
import Thanks from './Thanks/Thanks';
import { segmentTracking } from '../lib/tracker';
import Footnote from './Footnote/Footnote';
import { useCurrencyWithSymbol } from 'src/common/hooks/widgets/useCurrencyWithSymbol';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import {
  urxScriptLoader,
  urxScript,
} from 'src/common/hooks/widgets/globalDefinitions';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import { FormModal } from 'src/common/utils/FormModal';
export interface WatsonROICalculatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const WatsonROICalculator = ({
  langCode,
  test,
  countryToTest,
}: WatsonROICalculatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  // lang and userlocale is based out of ddo
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  /* Have urx form deault in us-en when on language us page */
  const [urxlang] = useState(localeCode.toString());
  const [localExist, setLocalExist] = useState(false);
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  // Set USD
  const [modalLocalCurrency, setModalLocalCurrency] = useState(
    LangDefaults.localCurrency,
  );
  // Set English language
  const [modalLocalLanguage, setModalLocalLanguage] = useState(
    LangDefaults.CountryLanguage,
  );
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  const intl = useIntl();
  const { bpName, logo, companyId, desc, mssp } = useBPName(UT30WAROI, true);
  const [clientsAmount, setClientsAmount] = useState(defaultClientsAmount);
  const [voiceAmount, setVoiceAmount] = useState(defaultVoiceAmount);
  const [environment] = useEnv();
  const cookieURL = useCookiesURL();
  const [formComplete, setFormComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [validInteraction, setValidInteraction] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const [formatClientAmount, clientInteractions] =
    useNumberFormat(clientsAmount);
  const clientPercentage = voiceAmount / 100;
  const [savedPerMonth, savedPerYear] = usePrice({
    clientInteractions,
    clientPercentage,
  });

  // currecny set to USD default
  const [monthlySaved, setMonthly] = useCurrencyWithSymbol(
    LangDefaults.localCurrency,
    lang,
    0,
  );
  const [yearlySaved, setYearly] = useCurrencyWithSymbol(
    LangDefaults.localCurrency,
    lang,
    0,
  );

  useEffect(() => {
    setMonthly(savedPerMonth);
    setYearly(savedPerYear);
  }, [savedPerMonth, savedPerYear]);

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  const colors = {
    Digital: colorDigital,
    Voice: colorVoice,
  };

  const graphOptions = {
    resizable: true,
    height: '240px',
    width: '100%',
    color: {
      scale: colors,
    },
    legend: {
      clickable: false,
    },
    toolbar: {
      enabled: false,
    },
  };

  useEffect(() => {
    setCookie();
  }, [formatClientAmount, voiceAmount, monthlySaved, yearlySaved]);

  const getBp = () => {
    if (bpName) return bpName;
    if (logo) return companyId;
    if (desc) return desc;
    if (mssp) return mssp;

    const bpField = document.querySelector('#Q_BPNAME') as HTMLInputElement;
    return bpField ? bpField.value : '';
  };

  const replaceBpField = () => {
    const bpField = document.getElementById('Q_BPNAME') as HTMLInputElement;

    if (bpName && bpField) {
      //update label text
      const bpLabel = bpField.labels?.length
        ? bpField.labels[0]
        : new HTMLInputElement();
      bpLabel.innerHTML = intl.formatMessage(messages.preferred_bp);

      //replace the bp field by the bp name
      const bpText = document.createElement('p') as HTMLInputElement;
      bpText.innerText = bpName;
      bpText.style.fontSize = '1rem';
      bpText.style.fontWeight = 'bold';
      bpText.style.lineHeight = '2.5';
      bpField.parentNode?.replaceChild(bpText, bpField);
    }
  };

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            messages['cookies-interactions-amount'].defaultMessage +
            ': ' +
            formatClientAmount +
            ' | ' +
            messages['cookie-voice'].defaultMessage +
            ': ' +
            voiceAmount +
            '%' +
            ' | ' +
            messages['cookie-montly-savings'].defaultMessage +
            ': ' +
            monthlySaved +
            ' | ' +
            messages['cookie-yearly-savings'].defaultMessage +
            ': ' +
            yearlySaved +
            (getBp() ? ` | Preferred BP: ${getBp()}` : ''),
          Q_ASSET_URL: window.location.href, // ISC - Asset URL
          Q_ASSET_NAME: 'IBM watsonx Assistant ROI Calculator', // ISC - Asset Name
        },
      }),
    );
    console.log(Cookies.get('urxdcq'));
  };

  const remaingAmount = 100 - Number(voiceAmount);
  const graphDataObj = {
    data: [
      { group: 'Voice', value: Number(voiceAmount) },
      { group: 'Digital', value: Number(remaingAmount) },
    ],
  };

  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
  ) => {
    const [formatClientAmount, originalClientAmount] = useNumberFormat(value);
    if (originalClientAmount >= 1 && originalClientAmount <= max) {
      setValue(formatClientAmount);
    } else if (originalClientAmount >= max) {
      const [formatClientAmount] = useNumberFormat(max);
      setValue(formatClientAmount);
    } else {
      setValue(0);
    }
  };

  // override the default behavior of cursor inputs

  useEffect(() => {
    const inputclientsAmount = document.getElementById(
      'clientAmount',
    ) as HTMLInputElement;
    const inputinteractionsAmount = document.getElementById(
      'interactions-input-for-slider',
    ) as HTMLInputElement;

    inputclientsAmount.addEventListener('input', (e) => {
      if (e != null) {
        const element = e.currentTarget as HTMLInputElement;
        let value: string = element.value.replaceAll(',', '');
        if (Number.parseInt(value) > maxClientsAmount) {
          value = maxClientsAmount.toString();
          setClientsAmount(maxClientsAmount);
        } else if (!value || Number.parseInt(value) < 1) {
          value = '1';
          setClientsAmount(1);
        }
      }
    });

    inputinteractionsAmount.addEventListener('input', (e) => {
      if (e != null) {
        const element = e.currentTarget as HTMLInputElement;
        let value: string = element.value.replaceAll(',', '');
        if (Number.parseInt(value) > maxVoiceAmount) {
          value = maxVoiceAmount.toString();
          setVoiceAmount(maxVoiceAmount);
        } else if (!value || Number.parseInt(value) < 1) {
          value = '';
          setVoiceAmount(0);
        }
      }
    });
  }, []);

  return (
    <div className="ibm-watson-calculator widget-styles">
      <Helmet>
        <link
          href="//1.www.s81c.com/common/v19a/css/www.css"
          rel="stylesheet"
        />
        <script src={urxScriptLoader}></script>
        <script src={urxScript}></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          voiceAmount={voiceAmount}
          clientsAmount={clientsAmount}
          monthlySaved={monthlySaved}
          yearlySaved={yearlySaved}
        />
      ) : (
        <div className="bx--grid" id="ibm-watson-grid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="bx--row">
                <div
                  className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12"
                  id="countryModal"
                >
                  {localExist && toggleCountry ? (
                    <ModalWrapper
                      modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage}`}
                      modalHeading={`The watsonx ROI Calculator is available in ${modalLocalLanguage}`}
                      size={'sm'}
                      primaryButtonText={'Cool! Take me there'}
                      secondaryButtonText={''}
                      buttonTriggerText={`Access page in ${modalLocalLanguage}`}
                      handleOpen={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: 'watsonx ROI Calculator Page',
                          object: 'watsonx ROI Calculator Page',
                          resultValue: 'CTA clicked',
                          CTA: 'watsonx ROI Title',
                          location: 'ROI Calculator',
                          action: '',
                          field: 'opened-access-button',
                        });
                      }}
                      handleSubmit={() => {
                        localStorage.setItem('langLocale', selectedLocale);
                        (window.location.href = window.location.href =
                          'https://www.ibm.com/' +
                          selectedLocale +
                          '/products/watson-assistant'),
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'watsonx ROI Calculator Page',
                            object: 'watsonx ROI Calculator Page',
                            resultValue: 'CTA clicked',
                            CTA: 'watsonx ROI Title',
                            location: 'ROI Calculator',
                            action: '',
                            field: 'submitted-access-button',
                          });
                        return true;
                      }}
                      onSecondarySubmit={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: 'watsonx ROI Calculator Page',
                          object: 'watsonx ROI Calculator Page',
                          resultValue: 'CTA clicked',
                          CTA: 'Stay here button clicked ' + modalLocalCountry,
                          location: 'ROI Calculator',
                          action: '',
                          field: 'stayhere-button-clicked',
                        });
                        return true;
                      }}
                    ></ModalWrapper>
                  ) : null}
                </div>
              </div>
              <div className="bx--row">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16 mar2">
                  <p className="page_sub_heading">
                    {' '}
                    <FormattedMessage {...messages['main-headline']} />
                  </p>
                  <p className="page_body padding_24_3 mar2">
                    <FormattedMessage {...messages['main-sub-headline']} />
                  </p>
                  {/*  User-Explorer-part  */}
                  <br />
                  <div className="bx--row padding_8">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: ' watsonx ROI Calculator Page',
                            object: 'watsonx ROI Calculator Page',
                            resultValue: 'CTA clicked',
                            CTA: 'Interactions tooltip',
                            location: 'Calculator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'left' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            <FormattedMessage
                              {...messages['label-question-one']}
                            />
                          </span>
                        }
                      >
                        <span className="slide_text">
                          <FormattedMessage {...messages['label-three']} />
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <div id="input-wrapper">
                        <TextInput
                          className="bx--text-input bx--text-input--lg"
                          id="clientAmount"
                          invalidText={
                            <FormattedMessage
                              {...messages['invalid-client_interactions']}
                            />
                          }
                          hideLabel={true}
                          labelText="Client Interactions"
                          placeholder={clientsAmount.toString()}
                          invalid={validInteraction}
                          onFocus={() => setClientsAmount(0)}
                          value={formatClientAmount}
                          onChange={(e) => {
                            const element = e.currentTarget as HTMLInputElement;
                            const value = element.value.replaceAll(',', '');
                            const regex = new RegExp('^[01-9,]*$');
                            const invalid =
                              !regex.test(value) && value.length > 0;
                            setValidInteraction(invalid);
                            if (!invalid) {
                              handleSliders(
                                Number.parseInt(value),
                                setClientsAmount,
                                maxClientsAmount,
                              );
                            } else {
                              setClientsAmount(0);
                            }
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'watsonx ROI Calculator Page',
                              object: 'watsonx ROI Calculator Page',
                              resultValue: 'CTA clicked',
                              CTA: 'client interactions ',
                              location: 'Calculator',
                              action: value,
                              field: 'client-interactions',
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <span className="page_body">
                        <FormattedMessage {...messages['labeltext-user']} />
                      </span>
                    </div>
                  </div>
                  <div className="bx--row mar1">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Slider
                        id="interactions"
                        min={minVoiceAmount}
                        max={maxVoiceAmount}
                        minLabel="%"
                        maxLabel="%"
                        value={defaultVoiceAmount}
                        onBlur={() => {
                          null;
                        }}
                        onChange={({ value }) => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'watsonx ROI Calculator Page',
                            object: 'watsonx ROI Calculator Page',
                            resultValue: 'CTA clicked',
                            CTA: 'Interactions voice / phone ',
                            location: 'Calculator',
                            action: value.toString(),
                            field: 'voice-interactions',
                          });
                          handleSliders(value, setVoiceAmount, maxVoiceAmount);
                        }}
                      />
                    </div>
                  </div>
                  <div id="footnote" className="div-only-lg">
                    <div className="bx--row">
                      <div className="bx--col-lg-16">
                        <Accordion align="start" size="sm">
                          <AccordionItem title="">
                            <Footnote numNote={1} />
                            <p className="_footnote-text">
                              <FormattedMessage {...messages['footnote-one']} />
                            </p>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--col-lg-1"></div>
                <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16">
                  <div id="stickyNav" className="bx--row">
                    <div className="sticky-nav price_table_comp">
                      {clientsAmount < 1000 ||
                      clientsAmount > 100000000 ? null : (
                        <div className="client-interactions">
                          <div className="page_sub_heading_2">
                            <FormattedMessage
                              {...messages['client-interactions']}
                            />
                            {formatClientAmount}
                          </div>

                          <PieChart
                            data={graphDataObj.data}
                            options={graphOptions}
                          ></PieChart>
                        </div>
                      )}
                      <div id="price_toggle">
                        {clientsAmount < 1000 || clientsAmount > 100000000 ? (
                          <>
                            <div className="page_body mar1">
                              <FormattedMessage
                                {...messages['text-not-in-range']}
                              />
                            </div>
                            <ButtonSet
                              className="ibm-watson_calculator__stack-button"
                              stacked
                            >
                              <Button
                                target="_self"
                                data-ibm-contact="scheduler-link"
                                href="javascript:void(0)"
                                id="request_btn"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'watsonx ROI Calculator Page',
                                    object: 'watsonx ROI Calculator Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Request a quote now',
                                    location: 'Calculator',
                                    action: '',
                                    field: '',
                                  });
                                  setCookie();
                                  initUrxForm(
                                    environment,
                                    URX_FORM.INSTANCE_ID,
                                    URX_FORM.FORM_ID,
                                    urxlang as IBMLocale,
                                    companyId,
                                  );
                                  setDrawerOpen(true);
                                }}
                              >
                                <FormattedMessage
                                  {...messages['talk-to-expert-button']}
                                />
                              </Button>
                            </ButtonSet>
                          </>
                        ) : (
                          <>
                            <hr className="ibm_watson_calculator__custom-hr mar1 mar3" />
                            <p
                              id="widget.estimated-cost"
                              className="price_table_comp__bodycopy"
                            >
                              <FormattedMessage
                                {...messages['estimated-costs-monthly']}
                              />
                              <sup className="ibm-watson-calculator__sup">
                                1
                              </sup>
                            </p>
                            <p className="padding_8 price_table_comp__price">
                              {clientsAmount < 1000 ? '' : monthlySaved}
                            </p>
                            <p
                              id="widget.estimated-cost"
                              className="price_table_comp__bodycopy mar3"
                            >
                              <FormattedMessage
                                {...messages['estimated-costs-yearly']}
                              />
                              <sup className="ibm-watson-calculator__sup">
                                1
                              </sup>
                            </p>
                            <p className="padding_8 price_table_comp__price mar1">
                              {clientsAmount < 1000 ? '' : yearlySaved}
                            </p>
                            <ButtonSet
                              className="ibm_watson_calculator__stack-button"
                              stacked
                            >
                              <Button
                                id="request_btn"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'watsonx ROI Calculator Page',
                                    object: 'watsonx ROI Calculator Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Request a quote now',
                                    location: 'Calculator',
                                    action: '',
                                    field: 'request-quote',
                                  });
                                  setCookie();
                                  initUrxForm(
                                    environment,
                                    URX_FORM.INSTANCE_ID,
                                    URX_FORM.FORM_ID,
                                    urxlang as IBMLocale,
                                    companyId,
                                  );
                                  setDrawerOpen(true);
                                  replaceBpField();
                                }}
                              >
                                <FormattedMessage
                                  {...messages['request-quote']}
                                />
                              </Button>
                            </ButtonSet>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div id="footnote" className="div-only-media">
                <div className="bx--row mar6">
                  <div className="bx--col-lg-9">
                    <div>
                      <Footnote numNote={1} />
                    </div>
                    <div className="footnote_text">
                      <FormattedMessage {...messages['footnote-one']} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <FormModal
        pageTitle="Watson ROI Page"
        object="Watson ROI Page"
        productTitle={'Watson ROI'}
        pageUrl={''}
        location={'ROI Calculator'}
        bpName={bpName}
        logo={logo}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
      ></FormModal>
    </div>
  );
};

export default WatsonROICalculator;
